import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { notifyModalOpen, notifyModalClose } from '../../../../utils/modalState';

interface BottomSheetProps {
  open: boolean;
  /** Omit for a drawer that renders its own header — a grab handle is shown instead of the title bar. */
  title?: string;
  onClose: () => void;
  children: React.ReactNode;
  /** false hides the X and ignores backdrop taps — used when the sheet should only close via its own explicit footer action (e.g. Save/Skip). Defaults to true. */
  dismissible?: boolean;
}

/** Shared bottom-sheet shell for the scoring pad's drawers — the pattern the spec calls out instead of a native `<select>`. */
export const BottomSheet: React.FC<BottomSheetProps> = ({ open, title, onClose, children, dismissible = true }) => {
  useEffect(() => {
    if (!open) return;
    notifyModalOpen();
    return () => notifyModalClose();
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end">
      <div className="absolute inset-0 bg-black/50" onClick={dismissible ? onClose : undefined} />
      <div
        className="relative w-full bg-white dark:bg-gray-900 rounded-t-3xl shadow-xl flex flex-col max-h-[85vh] overflow-hidden animate-slide-up"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {title ? (
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
            <span className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</span>
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                className="p-2 text-gray-500 dark:text-gray-400 rounded-full active:bg-gray-100 dark:active:bg-gray-800"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        ) : (
          <div className="flex-shrink-0 pt-2 pb-1">
            <div className="mx-auto w-10 h-1 rounded-full bg-gray-200 dark:bg-gray-700" aria-hidden />
          </div>
        )}
        <div className={`overflow-y-auto overscroll-contain ${title ? 'p-4' : 'px-4 pb-4 pt-2'}`}>{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default BottomSheet;
